import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import ResourceModalShell from './ResourceModalShell';
import { policyCategories } from '../content/policiesContent';

const HandbookModal = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load last viewed tab
  useEffect(() => {
    const lastTab = localStorage.getItem('handbook_last_tab');
    if (lastTab && policyCategories.find(c => c.id === lastTab)) {
      setSelectedCategory(lastTab);
    }
  }, []);

  // Save selected tab
  useEffect(() => {
    if (selectedCategory) {
      localStorage.setItem('handbook_last_tab', selectedCategory);
    }
  }, [selectedCategory]);

  // Get filtered content
  const getFilteredCategories = () => {
    if (!searchQuery.trim()) {
      return policyCategories;
    }

    const query = searchQuery.toLowerCase();
    return policyCategories.filter(cat => 
      cat.label.toLowerCase().includes(query) ||
      cat.content.toLowerCase().includes(query)
    );
  };

  const filteredCategories = getFilteredCategories();
  const currentCategory = policyCategories.find(c => c.id === selectedCategory);

  // Format markdown-style content
  const formatContent = (content) => {
    return content.split('\n').map((line, i) => {
      line = line.trim();
      
      // Headers
      if (line.startsWith('## ')) {
        return (
          <h3 key={i} style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#0A0A0A',
            margin: '24px 0 12px 0'
          }}>
            {line.substring(3)}
          </h3>
        );
      }
      
      if (line.startsWith('### ')) {
        return (
          <h4 key={i} style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            margin: '20px 0 8px 0'
          }}>
            {line.substring(4)}
          </h4>
        );
      }

      // Bullet points
      if (line.startsWith('- ')) {
        return (
          <li key={i} style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#374151',
            marginBottom: '6px'
          }}>
            {line.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}
          </li>
        );
      }

      // Regular paragraphs
      if (line.length > 0) {
        return (
          <p key={i} style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#374151',
            margin: '0 0 12px 0'
          }} dangerouslySetInnerHTML={{
            __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          }} />
        );
      }

      return null;
    });
  };

  return (
    <ResourceModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Company Handbook"
      subtitle="Policies & Procedures"
    >
      <div style={{
        display: 'flex',
        height: '100%',
        flexDirection: isMobile ? 'column' : 'row',
        overflow: 'hidden'
      }}>
        {/* Sidebar / Tab Selector */}
        {!isMobile ? (
          <div style={{
            width: '240px',
            borderRight: '1px solid #E5E7EB',
            padding: '16px',
            overflowY: 'auto'
          }}>
            {policyCategories.map(category => (
              <button
                key={category.id}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setSearchQuery('');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '4px',
                  background: selectedCategory === category.id ? '#EFF6FF' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: selectedCategory === category.id ? '600' : '400',
                  color: selectedCategory === category.id ? '#3B82F6' : '#374151',
                  transition: 'background 0.15s'
                }}
                onMouseOver={(e) => {
                  if (selectedCategory !== category.id) {
                    e.currentTarget.style.background = '#F9FAFB';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedCategory !== category.id) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '16px' }}>{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSearchQuery('');
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              {policyCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Content Area */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0
        }}>
          {/* Search Bar */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search
                size={18}
                color="#9CA3AF"
                style={{
                  position: 'absolute',
                  left: '12px'
                }}
              />
              <input
                type="text"
                placeholder="Search policies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Policy Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            padding: isMobile ? '16px' : '24px',
            minHeight: 0
          }}>
            {searchQuery.trim() ? (
              // Show search results
              filteredCategories.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#9CA3AF'
                }}>
                  <p style={{ margin: 0 }}>No results found for \"{searchQuery}\"</p>
                </div>
              ) : (
                filteredCategories.map(category => (
                  <div key={category.id} style={{ marginBottom: '32px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px'
                    }}>
                      <span style={{ fontSize: '20px' }}>{category.icon}</span>
                      <h3 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#0A0A0A'
                      }}>
                        {category.label}
                      </h3>
                    </div>
                    <div>{formatContent(category.content)}</div>
                  </div>
                ))
              )
            ) : (
              // Show selected category
              currentCategory && (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <span style={{ fontSize: '24px' }}>{currentCategory.icon}</span>
                    <h3 style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#0A0A0A'
                    }}>
                      {currentCategory.label}
                    </h3>
                  </div>
                  <div>{formatContent(currentCategory.content)}</div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </ResourceModalShell>
  );
};

export default HandbookModal;
